import React, { useState } from "react";
import {
  Card,
  Form,
  Input,
  DatePicker,
  Select,
  Button,
  message,
  Upload,
  Row,
  Col,
  Typography,
} from "antd";
import { UploadOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useCreate } from "@refinedev/core";
import { Actor } from "@/types";
import dayjs from "dayjs";

const { TextArea } = Input;
const { Title } = Typography;
const { Option } = Select;

export const ActorCreate: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const { mutate: createActor } = useCreate<Actor>();

  const onFinish = async (values: any) => {
    try {
      setLoading(true);

      const payload = {
        firstName: values.firstName?.trim(),
        lastName: values.lastName?.trim(),
        fullName: `${values.firstName?.trim()} ${values.lastName?.trim()}`.trim(),
        biography: values.biography?.trim(),
        birthDate: values.birthDate ? values.birthDate.format("YYYY-MM-DD") : null,
        nationality: values.nationality?.trim(),
        profileImageUrl: values.profileImageUrl?.trim(),
        gender: values.gender,
        profession: values.profession?.trim(),
      };

      createActor(
        {
          resource: "actors",
          values: payload,
        },
        {
          onSuccess: () => {
            message.success("Acteur créé avec succès");
            navigate("/artists");
          },
          onError: (error: any) => {
            console.error("Erreur lors de la création:", error);
            message.error(
              error?.response?.data?.message || "Erreur lors de la création de l'acteur"
            );
          },
        }
      );
    } catch (error: any) {
      console.error("Erreur:", error);
      message.error("Une erreur est survenue lors de la création");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: "24px" }}>
      <Card>
        <div style={{ marginBottom: 24 }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/artists")}
            style={{ marginBottom: 16 }}
          >
            Retour à la liste
          </Button>
          <Title level={2}>Créer un acteur</Title>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          autoComplete="off"
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="firstName"
                label="Prénom"
                rules={[
                  { required: true, message: "Le prénom est requis" },
                  { max: 100, message: "Maximum 100 caractères" },
                ]}
              >
                <Input placeholder="Prénom de l'acteur" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="lastName"
                label="Nom"
                rules={[
                  { required: true, message: "Le nom est requis" },
                  { max: 100, message: "Maximum 100 caractères" },
                ]}
              >
                <Input placeholder="Nom de l'acteur" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="gender"
                label="Genre"
                rules={[{ required: true, message: "Le genre est requis" }]}
              >
                <Select placeholder="Sélectionner le genre">
                  <Option value="MALE">Homme</Option>
                  <Option value="FEMALE">Femme</Option>
                  <Option value="OTHER">Autre</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="birthDate"
                label="Date de naissance"
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="DD/MM/YYYY"
                  placeholder="Sélectionner une date"
                  disabledDate={(current) => {
                    return current && current > dayjs().endOf("day");
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="nationality"
                label="Nationalité"
                rules={[{ max: 100, message: "Maximum 100 caractères" }]}
              >
                <Input placeholder="Ex: Sénégalais, Français, etc." />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="profession"
                label="Profession"
                rules={[{ max: 200, message: "Maximum 200 caractères" }]}
              >
                <Input placeholder="Ex: Acteur, Actrice, Réalisateur, etc." />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="profileImageUrl"
            label="URL de la photo de profil"
            rules={[
              { max: 500, message: "Maximum 500 caractères" },
              { type: "url", message: "URL invalide" },
            ]}
          >
            <Input placeholder="https://example.com/photo.jpg" />
          </Form.Item>

          <Form.Item
            name="biography"
            label="Biographie"
            rules={[{ max: 2000, message: "Maximum 2000 caractères" }]}
          >
            <TextArea
              rows={6}
              placeholder="Biographie de l'acteur..."
              showCount
              maxLength={2000}
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              size="large"
            >
              Créer l'acteur
            </Button>
            <Button
              style={{ marginLeft: 8 }}
              onClick={() => navigate("/artists")}
              size="large"
            >
              Annuler
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};
